import React, { useState, useCallback } from 'react';
import { Modal, Button, Alert, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Select from 'react-select';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import {
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  Loader2,
  X,
  Landmark
} from 'lucide-react';

const BankImportModal = ({ show, onHide, onImportSuccess }) => {
  const { companyId } = useParams();

  const banks = [
    { value: 'Swedbank', label: 'Swedbank' },
    { value: 'SEB', label: 'SEB' },
    { value: 'Handelsbanken', label: 'Handelsbanken' },
    { value: 'Nordea', label: 'Nordea' },
    { value: 'Danske Bank', label: 'Danske Bank' },
    { value: 'Länsförsäkringar Bank', label: 'Länsförsäkringar Bank' },
  ];

  const [selectedBank, setSelectedBank] = useState(null);
  const [file, setFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState(null);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setFileError(null);
    setImportResult(null);

    if (rejectedFiles && rejectedFiles.length > 0) {
      setFileError('Endast CSV-filer är tillåtna. Välj en fil som slutar med .csv');
      setFile(null);
      return;
    }

    if (acceptedFiles && acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];

      if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
        setFileError('Filen måste vara en CSV-fil (.csv)');
        setFile(null);
        return;
      }

      setFile(selectedFile);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
  });

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedBank) {
      setFileError('Välj en bank från rullgardinsmenyn.');
      return;
    }
    if (!file) {
      setFileError('Välj en CSV-fil för att importera.');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const apiUrl = getApiUrl(
        `${companyId}/accounting/reconciliation/banking/import?bankName=${encodeURIComponent(
          selectedBank.value
        )}`
      );
      const response = await axios.post(apiUrl, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);

      // If successful, call success callback after a short delay to show results
      if (response.data.success) {
        setTimeout(() => {
          if (onImportSuccess) {
            onImportSuccess();
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Error importing bank statement:', error);
      setImportResult({
        success: false,
        error: error.response?.data?.message || error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFile(null);
      setSelectedBank(null);
      setImportResult(null);
      setFileError(null);
      onHide();
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      border: '2px solid #e2e8f0',
      boxShadow: 'none',
      '&:hover': {
        border: '2px solid #cbd5e0',
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3182ce' : state.isFocused ? '#ebf8ff' : 'white',
      color: state.isSelected ? 'white' : '#2d3748',
    }),
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#eff6ff',
              color: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Landmark size={20} />
          </div>
          <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
            Importera Banktransaktioner
          </span>
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          onClick={handleClose}
          disabled={loading}
          aria-label="Close"
        />
      </Modal.Header>

      <Modal.Body className="px-4 py-4">
        {/* Bank Selection */}
        <div className="mb-4">
          <label className="form-label fw-semibold mb-2" style={{ fontSize: '0.875rem', color: '#374151' }}>
            <Landmark size={16} className="me-2" style={{ verticalAlign: 'middle' }} />
            Välj Bank
          </label>
          <Select
            options={banks}
            value={selectedBank}
            onChange={setSelectedBank}
            placeholder="Välj din bank..."
            styles={customSelectStyles}
            isDisabled={loading}
          />
        </div>

        {/* File Error Alert */}
        {fileError && (
          <Alert variant="warning" className="mb-3">
            <AlertTriangle size={16} className="me-2" />
            {fileError}
          </Alert>
        )}

        {/* File Drop Zone */}
        <div className="mb-4">
          <label className="form-label fw-semibold mb-2" style={{ fontSize: '0.875rem', color: '#374151' }}>
            <FileText size={16} className="me-2" style={{ verticalAlign: 'middle' }} />
            CSV-fil
          </label>
          <div
            {...getRootProps()}
            className={`border border-2 border-dashed rounded p-4 text-center ${
              isDragActive ? 'border-primary bg-light' : fileError ? 'border-danger' : 'border-secondary'
            }`}
            style={{ cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
          >
            <input {...getInputProps()} accept=".csv" disabled={loading} />
            <FileText
              size={48}
              className="mb-3"
              style={{ color: isDragActive ? '#3b82f6' : fileError ? '#dc3545' : '#6b7280' }}
            />
            {isDragActive ? (
              <p className="mb-0 text-primary" style={{ fontSize: '0.875rem' }}>
                Släpp CSV-filen här...
              </p>
            ) : (
              <div>
                <p className="mb-2" style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                  Dra och släpp en CSV-fil här, eller klicka för att välja
                </p>
                <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                  Accepterade format: .csv
                </p>
                {file && (
                  <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#f0fdf4' }}>
                    <p className="mb-0" style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: '500' }}>
                      <CheckCircle size={14} className="me-2" style={{ verticalAlign: 'middle' }} />
                      {file.name}
                    </p>
                    <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                      Storlek: {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Import Result */}
        {importResult && (
          <div className="mb-3">
            {importResult.success ? (
              <Alert variant="success" className="mb-0">
                <div className="d-flex">
                  <CheckCircle size={20} className="me-3 mt-1" />
                  <div className="flex-grow-1">
                    <h6 className="mb-2" style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                      Import lyckades!
                    </h6>
                    <p className="mb-2" style={{ fontSize: '0.875rem' }}>
                      {importResult.message || `${importResult.statistics?.successful || 0} transaktioner importerade.`}
                    </p>
                    {importResult.statistics && (
                      <div style={{ fontSize: '0.75rem' }}>
                        <div className="d-flex gap-3 mb-1">
                          <span><strong>Total:</strong> {importResult.statistics.total}</span>
                          <span className="text-success"><strong>Lyckade:</strong> {importResult.statistics.successful}</span>
                        </div>
                        {importResult.statistics.duplicates > 0 && (
                          <div className="text-warning mb-1">
                            <strong>Dubbletter:</strong> {importResult.statistics.duplicates} (hoppades över)
                          </div>
                        )}
                        {importResult.statistics.failed > 0 && (
                          <div className="text-danger">
                            <strong>Misslyckades:</strong> {importResult.statistics.failed}
                          </div>
                        )}
                      </div>
                    )}
                    {importResult.errors && importResult.errors.length > 0 && (
                      <details className="mt-2">
                        <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#6b7280' }}>
                          Visa fel ({importResult.errors.length})
                        </summary>
                        <ul className="mb-0 mt-2" style={{ fontSize: '0.75rem' }}>
                          {importResult.errors.slice(0, 10).map((err, index) => (
                            <li key={index}>
                              Rad {err.row}: {err.message || err.error}
                            </li>
                          ))}
                          {importResult.errors.length > 10 && (
                            <li className="text-muted">... och {importResult.errors.length - 10} fler</li>
                          )}
                        </ul>
                      </details>
                    )}
                  </div>
                </div>
              </Alert>
            ) : (
              <Alert variant="danger" className="mb-0">
                <div className="d-flex">
                  <AlertTriangle size={20} className="me-3 mt-1" />
                  <div>
                    <h6 className="mb-2" style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                      Import misslyckades
                    </h6>
                    <p className="mb-0" style={{ fontSize: '0.875rem' }}>
                      {importResult.error || importResult.message || 'Ett okänt fel uppstod'}
                    </p>
                  </div>
                </div>
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={loading}
        >
          {importResult?.success ? 'Stäng' : 'Avbryt'}
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={!file || !selectedBank || loading}
        >
          {loading ? (
            <>
              <Loader2 size={16} className="me-2" style={{ animation: 'spin 1s linear infinite' }} />
              Importerar...
            </>
          ) : (
            <>
              <Upload size={16} className="me-2" />
              Importera
            </>
          )}
        </Button>
      </Modal.Footer>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
};

export default BankImportModal;
